import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import AdminService from "../../../api/services/adminService";
import { downloadDownlineReport } from "./downlineViewExport";
import "../Home/Homepage.css";
import "./DownlineViewPage.css";
import {
  DOWNLINE_PAGE_SIZES,
  accessMessage,
  dash,
  formatDate,
  formatMoney,
  groupWallets,
  kycKind,
  readableType,
  sideKind,
  statusKind,
} from "./downlineViewShared";

const TABS = ["Overview", "Wallets", "Staking", "Commission", "Trades", "Downline"];
const LEVELS = ["1", "2", "3", "4", "5"];

const Chip = ({ kind = "muted", children }) => (
  <span className={`dv-chip dv-chip--${kind}`}>{children}</span>
);

const MoneyLines = ({ rows, amountKey = "amount" }) => {
  if (!Array.isArray(rows) || rows.length === 0) return <span className="dv-muted">—</span>;
  return (
    <div className="dv-money-stack">
      {rows.map((row, index) => (
        <div key={`${row.currency || "coin"}-${index}`}>{formatMoney(row[amountKey], row.currency)}</div>
      ))}
    </div>
  );
};

const Empty = ({ children }) => <p className="dv-empty">{children}</p>;

const WalletTables = ({ wallets, referralWallets }) => {
  const groups = groupWallets(wallets);
  const referrals = Array.isArray(referralWallets) ? referralWallets : [];
  if (!groups.length && !referrals.length) return <Empty>No wallets</Empty>;

  return (
    <>
      {groups.map((group) => (
        <section key={group.type} className="dv-section">
          <h3>{group.type}</h3>
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>Wallet</th>
                  <th>Coin</th>
                  <th>Balance</th>
                  <th>Locked</th>
                  <th>Bonus</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row, index) => (
                  <tr key={`${group.type}-${row.currency}-${index}`}>
                    <td>{dash(row.wallet_type)}</td>
                    <td>{dash(row.currency)}</td>
                    <td>{formatMoney(row.balance, row.currency)}</td>
                    <td>{formatMoney(row.locked_balance, row.currency)}</td>
                    <td>{formatMoney(row.bonus, row.currency)}</td>
                    <td>
                      <Chip kind={statusKind(row.status)}>{dash(row.status)}</Chip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      {referrals.length > 0 ? (
        <section className="dv-section">
          <h3>Referral wallet</h3>
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>Coin</th>
                  <th>Total earned</th>
                  <th>Locked</th>
                  <th>Available</th>
                  <th>Released</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((row, index) => (
                  <tr key={`${row.currency}-${index}`}>
                    <td>{dash(row.currency)}</td>
                    <td>{formatMoney(row.total_earned, row.currency)}</td>
                    <td>{formatMoney(row.locked_balance, row.currency)}</td>
                    <td>{formatMoney(row.available_balance, row.currency)}</td>
                    <td>{formatMoney(row.released_balance, row.currency)}</td>
                    <td>{formatMoney(row.balance, row.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
};

const StakingTable = ({ rows }) => {
  if (!Array.isArray(rows) || rows.length === 0) return <Empty>No staking plans</Empty>;
  return (
    <div className="dv-table-wrap">
      <table className="dv-table">
        <thead>
          <tr>
            <th>Package</th>
            <th>Coin</th>
            <th>Duration</th>
            <th>Return %</th>
            <th>Return type</th>
            <th>Invested</th>
            <th>Expected</th>
            <th>Paid</th>
            <th>Paid periods</th>
            <th>Remaining</th>
            <th>Status</th>
            <th>Payout</th>
            <th>Start</th>
            <th>End</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.subscription_id || `${row.package_name}-${row.start_date}`}>
              <td>{dash(row.package_name)}</td>
              <td>{dash(row.currency)}</td>
              <td>{row.duration_days != null ? `${row.duration_days} days` : "—"}</td>
              <td>{row.return_percentage == null ? "—" : `${row.return_percentage}%`}</td>
              <td>{dash(row.return_type)}</td>
              <td>{formatMoney(row.invested_amount, row.currency)}</td>
              <td>{formatMoney(row.expected_return, row.currency)}</td>
              <td>{formatMoney(row.paid_amount, row.currency)}</td>
              <td>{row.paid_days ?? "—"}</td>
              <td>{formatMoney(row.remaining_amount, row.currency)}</td>
              <td>
                <Chip kind={statusKind(row.status)}>{dash(row.status)}</Chip>
              </td>
              <td>{dash(row.payout_status)}</td>
              <td>{formatDate(row.start_date)}</td>
              <td>{formatDate(row.end_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TradesTable = ({ rows, showBrokerage }) => {
  if (!Array.isArray(rows) || rows.length === 0) return <Empty>No trades</Empty>;
  return (
    <div className="dv-table-wrap">
      <table className="dv-table">
        <thead>
          <tr>
            <th>Order id</th>
            <th>Pair</th>
            <th>Side</th>
            <th>Type</th>
            <th>Status</th>
            <th>Qty</th>
            <th>Filled</th>
            <th>Price</th>
            <th>Fee</th>
            <th>Fills</th>
            <th>Brokerage</th>
            <th>Time</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.order_db_id || row.order_id}>
              <td>{dash(row.order_id)}</td>
              <td>{dash(row.pair)}</td>
              <td>
                <Chip kind={sideKind(row.side)}>{dash(row.side)}</Chip>
              </td>
              <td>{dash(row.order_type)}</td>
              <td>
                <Chip kind={statusKind(row.status)}>{dash(row.status)}</Chip>
              </td>
              <td>{row.quantity ?? "—"}</td>
              <td>{row.filled ?? "—"}</td>
              <td>{row.price ?? "—"}</td>
              <td>{formatMoney(row.fee, row.fee_currency)}</td>
              <td>{row.fill_count ?? "—"}</td>
              <td>
                {showBrokerage
                  ? formatMoney(row.brokerage_to_searched_user, row.brokerage_currency)
                  : "—"}
              </td>
              <td>{formatDate(row.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AmountCell = ({ amount, currency, sourceAmount, sourceCurrency, creditedInZiln }) => (
  <div>
    <div>{formatMoney(amount, currency)}</div>
    {creditedInZiln && sourceAmount != null ? (
      <small className="dv-muted">from {formatMoney(sourceAmount, sourceCurrency)}</small>
    ) : null}
  </div>
);

const CommissionRecords = ({ commission, trades }) => {
  const staking = commission?.staking;
  const brokerage = commission?.brokerage;
  const referral = commission?.referral;
  const orderById = new Map((trades || []).map((trade) => [trade.order_db_id, trade.order_id]));

  return (
    <>
      <section className="dv-section">
        <div className="dv-subhead">
          <h3>Staking commission</h3>
          <small className="dv-muted">{staking?.count ?? 0} records</small>
        </div>
        <MoneyLines rows={staking?.total_by_currency} />
        {!staking?.records?.length ? (
          <Empty>No staking commission</Empty>
        ) : (
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Level</th>
                  <th>Amount</th>
                  <th>Source</th>
                  <th>Percent</th>
                  <th>Package coin</th>
                  <th>Duration</th>
                  <th>Invested</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {staking.records.map((row) => (
                  <tr key={row.id}>
                    <td>{readableType(row.type)}</td>
                    <td>{row.level ?? "—"}</td>
                    <td>
                      <AmountCell
                        amount={row.amount}
                        currency={row.currency}
                        sourceAmount={row.source_amount}
                        sourceCurrency={row.source_currency}
                        creditedInZiln={row.credited_in_ziln}
                      />
                    </td>
                    <td>{formatMoney(row.source_amount, row.source_currency)}</td>
                    <td>{row.commission_percent == null ? "—" : `${row.commission_percent}%`}</td>
                    <td>{dash(row.package_currency)}</td>
                    <td>{row.duration_days != null ? `${row.duration_days} days` : "—"}</td>
                    <td>{formatMoney(row.invested_amount, row.package_currency)}</td>
                    <td>{formatDate(row.credited_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="dv-section">
        <div className="dv-subhead">
          <h3>Brokerage</h3>
          <small className="dv-muted">{brokerage?.count ?? 0} records</small>
        </div>
        <MoneyLines rows={brokerage?.total_by_currency} />
        {!brokerage?.records?.length ? (
          <Empty>No brokerage</Empty>
        ) : (
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Amount</th>
                  <th>Source</th>
                  <th>Credit</th>
                  <th>Percent</th>
                  <th>Trade fee</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {brokerage.records.map((row) => (
                  <tr key={row.id}>
                    <td>{dash(orderById.get(row.order_db_id))}</td>
                    <td>
                      <AmountCell
                        amount={row.amount}
                        currency={row.currency}
                        sourceAmount={row.source_amount}
                        sourceCurrency={row.source_currency}
                        creditedInZiln={row.credited_in_ziln}
                      />
                    </td>
                    <td>{formatMoney(row.source_amount, row.source_currency)}</td>
                    <td>
                      <Chip kind={row.credited_in_ziln ? "ok" : "muted"}>
                        {row.credited_in_ziln ? "ZILN" : dash(row.currency)}
                      </Chip>
                    </td>
                    <td>{row.commission_percent == null ? "—" : `${row.commission_percent}%`}</td>
                    <td>{row.trade_fee ?? "—"}</td>
                    <td>{formatDate(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="dv-section">
        <div className="dv-subhead">
          <h3>Referral commission</h3>
          <small className="dv-muted">{referral?.count ?? 0} records</small>
        </div>
        <MoneyLines rows={referral?.total_by_currency} />
        <p className="dv-hint">
          Signup bonus:{" "}
          {referral?.signup_bonus_by_currency?.length
            ? referral.signup_bonus_by_currency.map((row) => formatMoney(row.amount, row.currency)).join(" · ")
            : "—"}
        </p>
        <p className="dv-hint">
          Referral bonus:{" "}
          {referral?.referral_bonus_by_currency?.length
            ? referral.referral_bonus_by_currency.map((row) => formatMoney(row.amount, row.currency)).join(" · ")
            : "—"}
        </p>
        {!referral?.records?.length ? (
          <Empty>No referral commission</Empty>
        ) : (
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Level</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {referral.records.map((row) => (
                  <tr key={row.id}>
                    <td>{readableType(row.type)}</td>
                    <td>{row.level ?? "—"}</td>
                    <td>{formatMoney(row.amount, row.currency)}</td>
                    <td>{formatDate(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};

const MemberPanel = ({ member }) => {
  const [inner, setInner] = useState("Wallets");
  return (
    <div className="dv-expand-box">
      <div className="dv-tabs">
        {["Wallets", "Staking", "Commission", "Trades"].map((tab) => (
          <button
            key={tab}
            type="button"
            className={`dv-tab ${inner === tab ? "dv-tab--active" : ""}`}
            onClick={() => setInner(tab)}
          >
            {tab}
          </button>
        ))}
      </div>
      {inner === "Wallets" ? (
        <WalletTables wallets={member.wallets} referralWallets={member.referral_wallets} />
      ) : null}
      {inner === "Staking" ? <StakingTable rows={member.staking} /> : null}
      {inner === "Commission" ? (
        <CommissionRecords commission={member.commission_to_searched_user} trades={member.trades} />
      ) : null}
      {inner === "Trades" ? <TradesTable rows={member.trades} showBrokerage /> : null}
    </div>
  );
};

const StakingCell = ({ totals }) => {
  if (!Array.isArray(totals) || totals.length === 0) return "—";
  return (
    <div className="dv-money-stack">
      {totals.map((row) => (
        <div key={row.currency}>
          <div>{formatMoney(row.invested_amount, row.currency)} invested</div>
          <small className="dv-muted">{formatMoney(row.paid_amount, row.currency)} paid</small>
        </div>
      ))}
    </div>
  );
};

const ProfileGrid = ({ profile }) => {
  const sponsor =
    profile?.sponsor_name || profile?.sponsor_uuid
      ? [profile.sponsor_name, profile.sponsor_uuid].filter(Boolean).join(" · ")
      : "—";
  return (
    <dl className="dv-profile">
      <div>
        <dt>Email</dt>
        <dd>{dash(profile?.email)}</dd>
      </div>
      <div>
        <dt>Mobile</dt>
        <dd>{dash(profile?.mobile)}</dd>
      </div>
      <div>
        <dt>Referral code</dt>
        <dd>{dash(profile?.referral_code)}</dd>
      </div>
      <div>
        <dt>Status</dt>
        <dd>{dash(profile?.account_status)}</dd>
      </div>
      <div>
        <dt>KYC</dt>
        <dd>{dash(profile?.kyc_label)}</dd>
      </div>
      <div>
        <dt>Payout</dt>
        <dd>{dash(profile?.payout_status)}</dd>
      </div>
      <div>
        <dt>Registered</dt>
        <dd>{formatDate(profile?.registered_at)}</dd>
      </div>
      <div>
        <dt>Sponsor</dt>
        <dd>{sponsor}</dd>
      </div>
      <div>
        <dt>Direct referrals</dt>
        <dd>{profile?.total_direct_referrals ?? 0}</dd>
      </div>
    </dl>
  );
};

const Overview = ({ user, summary }) => {
  const commission = summary?.commission_received_from_downline || user?.commission_received_from_downline;
  const own = user?.own_referral_rewards;
  const levels = summary?.by_level || {};

  return (
    <>
      <ProfileGrid profile={user?.profile} />

      <section className="dv-section">
        <h2>Downline</h2>
        <div className="dv-cards">
          <div className="dv-stat">
            <span>Downline count</span>
            <strong>{summary?.downline_count ?? 0}</strong>
          </div>
          {LEVELS.map((level) => (
            <div className="dv-stat" key={level}>
              <span>Level {level}</span>
              <strong>{levels[level] ?? levels[Number(level)] ?? 0}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="dv-section">
        <h2>Wallet totals</h2>
        {!user?.wallet_totals?.length ? (
          <Empty>No wallet totals</Empty>
        ) : (
          <div className="dv-cards">
            {user.wallet_totals.map((row) => (
              <div className="dv-stat" key={row.currency}>
                <span>{row.currency}</span>
                <strong>{formatMoney(row.balance, row.currency)}</strong>
                <small>Locked {formatMoney(row.locked_balance, row.currency)}</small>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="dv-section">
        <h2>Staking totals</h2>
        {!user?.staking_totals?.length ? (
          <Empty>No staking totals</Empty>
        ) : (
          <div className="dv-cards">
            {user.staking_totals.map((row) => (
              <div className="dv-stat" key={row.currency}>
                <span>
                  {row.currency} · {row.plans ?? 0} plans
                </span>
                <strong>{formatMoney(row.invested_amount, row.currency)} invested</strong>
                <small>
                  Paid {formatMoney(row.paid_amount, row.currency)} · Remaining{" "}
                  {formatMoney(row.remaining_amount, row.currency)}
                </small>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="dv-section">
        <h2>Commission from downline</h2>
        <div className="dv-cards">
          <div className="dv-stat">
            <span>Staking commission</span>
            <strong>
              <MoneyLines rows={commission?.staking?.total_by_currency} />
            </strong>
            <small>{commission?.staking?.count ?? 0} records</small>
          </div>
          <div className="dv-stat">
            <span>Brokerage</span>
            <strong>
              <MoneyLines rows={commission?.brokerage?.total_by_currency} />
            </strong>
            <small>{commission?.brokerage?.count ?? 0} records</small>
          </div>
          <div className="dv-stat">
            <span>Referral commission</span>
            <strong>
              <MoneyLines rows={commission?.referral?.total_by_currency} />
            </strong>
            <small>{commission?.referral?.count ?? 0} records</small>
            <small>
              Signup bonus:{" "}
              {commission?.signup_bonus?.total_by_currency?.length
                ? commission.signup_bonus.total_by_currency
                    .map((row) => formatMoney(row.amount, row.currency))
                    .join(" · ")
                : "—"}
            </small>
            <small>
              Referral bonus:{" "}
              {commission?.referral_bonus?.total_by_currency?.length
                ? commission.referral_bonus.total_by_currency
                    .map((row) => formatMoney(row.amount, row.currency))
                    .join(" · ")
                : "—"}
            </small>
          </div>
        </div>
      </section>

      <section className="dv-section">
        <h2>Own signup bonus</h2>
        <div className="dv-cards">
          <div className="dv-stat">
            <span>Own referral rewards</span>
            <strong>
              <MoneyLines rows={own?.total_by_currency} />
            </strong>
            <small>{own?.count ?? 0} records</small>
          </div>
        </div>
        {own?.records?.length ? (
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Level</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {own.records.map((row) => (
                  <tr key={row.id}>
                    <td>{readableType(row.type)}</td>
                    <td>{row.level ?? "—"}</td>
                    <td>{formatMoney(row.amount, row.currency)}</td>
                    <td>{formatDate(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
};

const CommissionTab = ({ commission }) => (
  <div className="dv-cards">
    <div className="dv-stat">
      <span>Staking commission</span>
      <strong>
        <MoneyLines rows={commission?.staking?.total_by_currency} />
      </strong>
      <small>{commission?.staking?.count ?? 0} records</small>
    </div>
    <div className="dv-stat">
      <span>Brokerage</span>
      <strong>
        <MoneyLines rows={commission?.brokerage?.total_by_currency} />
      </strong>
      <small>{commission?.brokerage?.count ?? 0} records</small>
    </div>
    <div className="dv-stat">
      <span>Referral commission</span>
      <strong>
        <MoneyLines rows={commission?.referral?.total_by_currency} />
      </strong>
      <small>{commission?.referral?.count ?? 0} records</small>
      <small>
        Signup bonus:{" "}
        {commission?.signup_bonus?.total_by_currency?.length
          ? commission.signup_bonus.total_by_currency.map((row) => formatMoney(row.amount, row.currency)).join(" · ")
          : "—"}
      </small>
      <small>
        Referral bonus:{" "}
        {commission?.referral_bonus?.total_by_currency?.length
          ? commission.referral_bonus.total_by_currency.map((row) => formatMoney(row.amount, row.currency)).join(" · ")
          : "—"}
      </small>
    </div>
  </div>
);

const DownlineViewDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [level, setLevel] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedId, setExpandedId] = useState("");
  const requestSeq = useRef(0);

  const load = useCallback(async (nextLevel) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    const params = nextLevel ? { level: nextLevel } : {};
    const res = await AdminService.getDownlineView(userId, params);
    if (seq !== requestSeq.current) return { ok: false, ignored: true };
    setLoading(false);
    if (res?.success && res.data) {
      setReport(res.data);
      setError("");
      setNotFound(false);
      return { ok: true };
    }
    const message = accessMessage(res, "Failed to load downline view");
    return { ok: false, status: res?.httpStatus, message };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    setTab("Overview");
    setLevel("");
    setPage(1);
    setExpandedId("");
    setReport(null);
    setNotFound(false);
    setError("");
    load("").then((result) => {
      if (cancelled || result.ignored || result.ok) return;
      setError(result.message);
      if (result.status === 404 || result.message === "User not found") setNotFound(true);
    });
    return () => {
      cancelled = true;
      requestSeq.current += 1;
    };
  }, [load]);

  const user = report?.searched_user;
  const profile = user?.profile;
  const downline = useMemo(() => (Array.isArray(report?.downline) ? report.downline : []), [report]);
  const pageCount = Math.max(1, Math.ceil(downline.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = downline.slice((safePage - 1) * pageSize, safePage * pageSize);

  const goBack = () => navigate("/dashboard/referral/downline-view");

  const changeLevel = async (nextLevel) => {
    if (nextLevel === level || loading) return;
    const previous = level;
    setLevel(nextLevel);
    setPage(1);
    setExpandedId("");
    const result = await load(nextLevel);
    if (result.ignored || result.ok) return;
    setLevel(previous);
    toast.error(result.message || "Failed to load downline view");
  };

  const downloadExcel = async () => {
    if (!report || downloading) return;
    setDownloading(true);
    try {
      await downloadDownlineReport({ report, level });
    } catch (err) {
      toast.error(err?.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (notFound) {
    return (
      <main className="dashboard-main dashboard-subpage dv-page">
        <div className="dashboard-body dashboard-content-inner">
          <p className="dv-error">{error || "User not found"}</p>
          <button type="button" className="dv-btn dv-btn--ghost" onClick={goBack}>
            Back
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="dashboard-main dashboard-subpage dv-page">
      <div className="dashboard-body dashboard-content-inner">
        <div className="dv-detail-head">
          <div>
            <button type="button" className="dv-btn dv-btn--ghost dv-btn--small" onClick={goBack}>
              ← Back
            </button>
            <h1>{profile?.name || (loading ? "Loading..." : "Downline View")}</h1>
            {profile ? (
              <div className="dv-meta">
                <span>UUID {dash(profile.uuid)}</span>
                <Chip kind={statusKind(profile.account_status)}>{dash(profile.account_status)}</Chip>
                <Chip kind={kycKind(profile.kyc_label)}>KYC {dash(profile.kyc_label)}</Chip>
              </div>
            ) : null}
          </div>
          <button type="button" className="dv-btn" disabled={!report || loading || downloading} onClick={downloadExcel}>
            {downloading ? "Downloading..." : "Download Excel"}
          </button>
        </div>

        {error && !report ? <p className="dv-error">{error}</p> : null}
        {loading && !report ? <p className="dv-hint">Loading downline view...</p> : null}

        {report ? (
          <>
            <div className="dv-tabs">
              {TABS.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`dv-tab ${tab === name ? "dv-tab--active" : ""}`}
                  onClick={() => setTab(name)}
                >
                  {name}
                </button>
              ))}
            </div>

            {tab === "Overview" ? <Overview user={user} summary={report.summary} /> : null}
            {tab === "Wallets" ? (
              <WalletTables wallets={user?.wallets} referralWallets={user?.referral_wallets} />
            ) : null}
            {tab === "Staking" ? <StakingTable rows={user?.staking} /> : null}
            {tab === "Commission" ? (
              <CommissionTab commission={user?.commission_received_from_downline} />
            ) : null}
            {tab === "Trades" ? <TradesTable rows={user?.trades} showBrokerage={false} /> : null}
            {tab === "Downline" ? (
              <>
                <div className="dv-level-row">
                  <button
                    type="button"
                    className={`dv-tab ${level === "" ? "dv-tab--active" : ""}`}
                    onClick={() => changeLevel("")}
                  >
                    All
                  </button>
                  {LEVELS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={`dv-tab ${level === item ? "dv-tab--active" : ""}`}
                      onClick={() => changeLevel(item)}
                    >
                      Level {item}
                      {report.summary?.by_level
                        ? ` (${report.summary.by_level[item] ?? report.summary.by_level[Number(item)] ?? 0})`
                        : ""}
                    </button>
                  ))}
                </div>
                {loading ? <p className="dv-hint">Loading downline...</p> : null}
                {!downline.length ? (
                  <Empty>No downline users</Empty>
                ) : (
                  <>
                    <div className="dv-table-wrap">
                      <table className="dv-table">
                        <thead>
                          <tr>
                            <th>Level</th>
                            <th>Name</th>
                            <th>UUID</th>
                            <th>Email</th>
                            <th>Mobile</th>
                            <th>KYC</th>
                            <th>Status</th>
                            <th>Sponsor</th>
                            <th>Staking</th>
                            <th>Staking commission</th>
                            <th>Brokerage</th>
                            <th>Referral commission</th>
                            <th>Trades</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.map((member) => {
                            const id = member?.profile?.user_id;
                            const open = expandedId === id;
                            const commission = member?.commission_to_searched_user;
                            return (
                              <React.Fragment key={id || member?.profile?.uuid}>
                                <tr>
                                  <td>{member?.profile?.level ?? "—"}</td>
                                  <td>{dash(member?.profile?.name)}</td>
                                  <td>{dash(member?.profile?.uuid)}</td>
                                  <td>{dash(member?.profile?.email)}</td>
                                  <td>{dash(member?.profile?.mobile)}</td>
                                  <td>
                                    <Chip kind={kycKind(member?.profile?.kyc_label)}>
                                      {dash(member?.profile?.kyc_label)}
                                    </Chip>
                                  </td>
                                  <td>
                                    <Chip kind={statusKind(member?.profile?.account_status)}>
                                      {dash(member?.profile?.account_status)}
                                    </Chip>
                                  </td>
                                  <td>{dash(member?.profile?.sponsor_name)}</td>
                                  <td>
                                    <StakingCell totals={member?.staking_totals} />
                                  </td>
                                  <td>
                                    <MoneyLines rows={commission?.staking?.total_by_currency} />
                                  </td>
                                  <td>
                                    <MoneyLines rows={commission?.brokerage?.total_by_currency} />
                                  </td>
                                  <td>
                                    <MoneyLines rows={commission?.referral?.total_by_currency} />
                                  </td>
                                  <td>{member?.trade_count ?? 0}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="dv-btn dv-btn--ghost dv-btn--small"
                                      onClick={() => setExpandedId(open ? "" : id)}
                                    >
                                      {open ? "−" : "+"}
                                    </button>
                                  </td>
                                </tr>
                                {open ? (
                                  <tr className="dv-expand">
                                    <td colSpan={14}>
                                      <MemberPanel member={member} />
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="dv-pager">
                      <label>
                        Rows
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value) || 20);
                            setPage(1);
                            setExpandedId("");
                          }}
                        >
                          {DOWNLINE_PAGE_SIZES.map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </label>
                      <span>
                        Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, downline.length)} of{" "}
                        {downline.length}
                      </span>
                      <div>
                        <button
                          type="button"
                          className="dv-btn dv-btn--ghost dv-btn--small"
                          disabled={safePage <= 1}
                          onClick={() => {
                            setPage(safePage - 1);
                            setExpandedId("");
                          }}
                        >
                          Prev
                        </button>
                        <button
                          type="button"
                          className="dv-btn dv-btn--ghost dv-btn--small"
                          disabled={safePage >= pageCount}
                          onClick={() => {
                            setPage(safePage + 1);
                            setExpandedId("");
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </main>
  );
};

export default DownlineViewDetailPage;
